import { DatePipe, NgClass, TitleCasePipe } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { IconFieldModule } from '@openng/optimus-ui/iconfield';
import { InputIconModule } from '@openng/optimus-ui/inputicon';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { TableModule } from '@openng/optimus-ui/table';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { DateFormatPipe, FirstCapitalPipe, SvgIconsComponent } from '@zayan-erp/shared';
import { environment } from '../../../../../../apps/cinari-erp-client/src/environments/environment';
import { FileActivityService } from '../../services/file-activity.service';

@Component({
  selector: 'app-activity-log-modal',
  standalone: true,
  imports: [
    DatePipe,
    TitleCasePipe,
    NgClass,
    FormsModule,
    DatePickerModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TableModule,
    SvgIconsComponent,
    FirstCapitalPipe,
    DateFormatPipe
  ],
  templateUrl: './activity-log-modal.component.html',
  styleUrl: './activity-log-modal.component.scss'
})
export class ActivityLogModalComponent implements OnInit, OnDestroy {
  @Input() fileId = '';

  activeModal = inject(NgbActiveModal);
  #fileActivityService = inject(FileActivityService);

  basicFiles = environment.basicFiles;
  revitFiles = environment.revitFiles;
  cadFiles = environment.cadFiles;

  activityLogs = signal<any[]>([]);
  isLoadingActivityLogs = signal(false);
  activityLogSearch = signal('');
  activityLogFromDate = signal<Date>(this.#daysAgo(3));
  activityLogToDate = signal<Date>(this.#daysAgo(0));

  #destroy = new Subject<void>();
  #searchTerms = new Subject<string>();

  ngOnInit(): void {
    this.#searchTerms.pipe(debounceTime(400), takeUntil(this.#destroy)).subscribe(term => {
      this.activityLogSearch.set(term);
      this.#loadActivityLogs();
    });

    this.#loadActivityLogs();
  }

  ngOnDestroy(): void {
    this.#destroy.next();
    this.#destroy.complete();
  }

  onSearchChange(term: string): void {
    this.#searchTerms.next(term);
  }

  onFromDateChange(date: Date): void {
    this.activityLogFromDate.set(date);
    this.#loadActivityLogs();
  }

  onToDateChange(date: Date): void {
    this.activityLogToDate.set(date);
    this.#loadActivityLogs();
  }

  formatActivityLabel(activity: string): string {
    return activity?.replace('File ', '').replace('Folder ', '') ?? '';
  }

  activityStatusClass(activity: string): Record<string, boolean> {
    const label = this.formatActivityLabel(activity);
    return {
      'alert-warning': label === 'Renamed' || label === 'Updated',
      'alert-danger': label === 'Deleted' || label === 'Permanent Deleted' || label === 'Permanently Deleted',
      'alert-primary': label === 'Notes Updated',
      'alert-info': label === 'Created' || label === 'Restored' || label === 'Shared' || label === 'Moved',
      'alert-success': label === 'Uploaded' || label === 'Accessed',
      'alert-secondary': label === 'Unshared' || label === 'Link Created'
    };
  }

  getActivityIcon(fileSystemName: string): string {
    if (!fileSystemName?.includes('.')) {
      return 'ft-folder';
    }

    const extension = fileSystemName.split('.').pop()?.toLowerCase() ?? '';
    if (this.basicFiles.includes(extension)) {
      return `ft-${extension}`;
    }
    if (this.revitFiles.includes(extension)) {
      return 'ft-revit';
    }
    if (this.cadFiles.includes(extension)) {
      return 'ft-cad';
    }
    return 'ft-file';
  }

  #daysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  #formatActivityDate(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  #loadActivityLogs(): void {
    this.isLoadingActivityLogs.set(true);
    this.#fileActivityService
      .getActivityLogs({
        searchText: this.activityLogSearch(),
        fileSystemId: this.fileId,
        fromDate: this.#formatActivityDate(this.activityLogFromDate()),
        toDate: this.#formatActivityDate(this.activityLogToDate()),
        pageSize: 1000
      })
      .pipe(takeUntil(this.#destroy))
      .subscribe({
        next: data => {
          this.activityLogs.set(data ?? []);
          this.isLoadingActivityLogs.set(false);
        },
        error: () => this.isLoadingActivityLogs.set(false)
      });
  }
}
